// src/supplier/pages/SupplierRFQs.jsx
import { Fragment, useEffect, useState, useCallback, useMemo } from "react";
import { supplierRFQAPI } from "../services/supplierApi";
import {
  FileText, Loader2, ChevronLeft, Send, AlertCircle,
  Calendar, DollarSign, Package, Clock, Info, Building2,
  Tag, Layers, ChevronDown, ChevronUp, Hash
} from "lucide-react";
import Swal from "../../utils/apparifySwal";
import { fmtPeso, fmtDate } from "../../utils/formatters";
import { StatusBadge } from "../../components/erp";
import { getRFQLabel } from "../../utils/documentLabels";

/* ─── Helpers ──────────────────────────────────────────── */

// Returns the end of the deadline day (23:59:59.999) so that "due today" means
// the supplier can still submit until midnight — not just until 00:00:00.
function endOfDeadlineDay(deadlineAt) {
  const d = new Date(deadlineAt);
  d.setHours(23, 59, 59, 999);
  return d;
}

function deadlineInfo(deadlineAt) {
  if (!deadlineAt) return null;
  // Compare calendar dates only (ignore stored time) so "today" == "today" regardless
  // of whether the DB stored midnight or some other time component.
  const now = new Date();
  const nowDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dl = new Date(deadlineAt);
  const dlDay = new Date(dl.getFullYear(), dl.getMonth(), dl.getDate());
  const diffDays = Math.round((dlDay - nowDay) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return { label: "Overdue", className: "text-red-600 dark:text-red-400" };
  if (diffDays === 0) return { label: "Due today", className: "text-amber-600 dark:text-amber-400" };
  if (diffDays <= 3) return { label: `${diffDays}d left`, className: "text-amber-600 dark:text-amber-400" };
  return { label: `${diffDays}d left`, className: "text-neutral-500 dark:text-neutral-400" };
}

function SectionCard({ children, className = "" }) {
  return (
    <div className={`operations-surface p-5 ${className}`}>
      {children}
    </div>
  );
}

function InfoRow({ icon: Icon, label, value, muted }) {
  return (
    <div className="flex items-start gap-2.5 text-xs">
      {Icon && <Icon className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-neutral-400 dark:text-neutral-500" />}
      <div>
        <p className="text-neutral-400 dark:text-neutral-500 mb-0.5">{label}</p>
        <p className={`font-medium ${muted ? "text-neutral-400 dark:text-neutral-500" : "text-neutral-700 dark:text-neutral-200"}`}>
          {value || "—"}
        </p>
      </div>
    </div>
  );
}

/* ─── Variant Breakdown Table ──────────────────────────── */

function VariantBreakdown({ items, productName }) {
  const [expanded, setExpanded] = useState(true);
  const safeItems = useMemo(() => items || [], [items]);
  const totalQty = safeItems.reduce((sum, i) => sum + Number(i.requestedQuantity || i.requested_quantity || 0), 0);
  // Collect all unique option names across items for column headers
  const optionNames = useMemo(() => {
    const names = new Set();
    safeItems.forEach((i) => Object.keys(i.optionValues || {}).forEach((k) => names.add(k)));
    return [...names];
  }, [safeItems]);

  if (!safeItems.length) return null;

  return (
    <SectionCard>
      <button
        type="button"
        onClick={() => setExpanded((p) => !p)}
        className="flex w-full items-center justify-between"
      >
        <div className="flex items-center gap-2">
          <Layers className="h-4 w-4 text-indigo-500" />
          <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-50">
            Requested Items
          </h3>
          <span className="rounded-full bg-indigo-100 dark:bg-indigo-900/40 px-2 py-0.5 text-[10px] font-semibold text-indigo-700 dark:text-indigo-300">
            {safeItems.length} variant{safeItems.length !== 1 ? "s" : ""} · {totalQty} total units
          </span>
        </div>
        {expanded ? <ChevronUp className="h-4 w-4 text-neutral-400" /> : <ChevronDown className="h-4 w-4 text-neutral-400" />}
      </button>

      {expanded && (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-neutral-100 dark:border-neutral-800 text-neutral-400 dark:text-neutral-500">
                <th className="pb-2 pr-3 text-left font-medium">#</th>
                <th className="pb-2 pr-3 text-left font-medium">Variant</th>
                {optionNames.map((n) => (
                  <th key={n} className="pb-2 pr-3 text-left font-medium">{n}</th>
                ))}
                <th className="pb-2 pr-3 text-left font-medium">SKU</th>
                <th className="pb-2 text-right font-medium">Qty</th>
              </tr>
            </thead>
            <tbody>
              {safeItems.map((item, idx) => (
                <tr key={item.id} className="border-b border-neutral-50 dark:border-neutral-800/50 last:border-0">
                  <td className="py-2 pr-3 text-neutral-400">{idx + 1}</td>
                  <td className="py-2 pr-3 font-medium text-neutral-700 dark:text-neutral-200">
                    {item.variantTitle || item.variant_title || productName || "—"}
                  </td>
                  {optionNames.map((n) => (
                    <td key={n} className="py-2 pr-3 text-neutral-600 dark:text-neutral-300">
                      <span className="inline-flex rounded bg-neutral-100 dark:bg-neutral-800 px-1.5 py-0.5">
                        {item.optionValues?.[n] || "—"}
                      </span>
                    </td>
                  ))}
                  <td className="py-2 pr-3 text-neutral-500 dark:text-neutral-400 font-mono text-[11px]">
                    {item.variantSku || item.variant_sku || "—"}
                  </td>
                  <td className="py-2 text-right font-semibold text-neutral-800 dark:text-neutral-100">
                    {item.requestedQuantity || item.requested_quantity}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-neutral-200 dark:border-neutral-700">
                <td colSpan={2 + optionNames.length + 1} className="py-2 text-right font-medium text-neutral-500 dark:text-neutral-400">
                  Total
                </td>
                <td className="py-2 text-right font-bold text-neutral-900 dark:text-neutral-50">
                  {totalQty}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </SectionCard>
  );
}

/* ─── Quote Summary (live computation) ─────────────────── */

function QuoteSummary({ unitPrice, totalQty }) {
  const unitCents = Math.round(parseFloat(unitPrice || 0) * 100);
  if (!unitCents || unitCents <= 0 || !totalQty) return null;
  const totalCents = unitCents * totalQty;
  return (
    <div className="rounded-lg bg-blue-50 dark:bg-blue-950/30 p-3 text-xs space-y-1">
      <div className="flex justify-between">
        <span className="text-blue-600 dark:text-blue-400">Unit Price</span>
        <span className="font-medium text-blue-800 dark:text-blue-200">{fmtPeso(unitCents)}</span>
      </div>
      <div className="flex justify-between">
        <span className="text-blue-600 dark:text-blue-400">× Quantity</span>
        <span className="font-medium text-blue-800 dark:text-blue-200">{totalQty} units</span>
      </div>
      <div className="flex justify-between border-t border-blue-200 dark:border-blue-800 pt-1">
        <span className="font-semibold text-blue-700 dark:text-blue-300">Estimated Total</span>
        <span className="font-bold text-blue-900 dark:text-blue-100">{fmtPeso(totalCents)}</span>
      </div>
    </div>
  );
}

function sanitizeIntegerInput(value) {
  return String(value || "").replace(/\D/g, "");
}

function sanitizePriceInput(value) {
  const cleaned = String(value || "").replace(/[^\d.]/g, "");
  const parts = cleaned.split(".");
  if (parts.length === 1) return cleaned;
  return `${parts[0]}.${parts.slice(1).join("").slice(0, 2)}`;
}

function deriveDeliveryDays(expectedDeliveryAt) {
  if (!expectedDeliveryAt) return "";
  const now = new Date();
  const deliveryDate = new Date(expectedDeliveryAt);
  const nowDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const deliveryDay = new Date(deliveryDate.getFullYear(), deliveryDate.getMonth(), deliveryDate.getDate());
  const diffDays = Math.round((deliveryDay - nowDay) / (1000 * 60 * 60 * 24));
  return diffDays > 0 ? String(diffDays) : "";
}

const QUOTEABLE_STATUSES = ["SENT", "OPEN_FOR_QUOTES", "PARTIALLY_QUOTED", "QUOTES_RECEIVED"];
const OPEN_INVITATION_STATUSES = ["SENT", "VIEWED"];

/* ─── Submitted Quote Display ─────────────────────────── */

function SubmittedQuote({ quote, totalQty }) {
  const lineTotalCents = Array.isArray(quote.lines) && quote.lines.length
    ? quote.lines.reduce((sum, line) => sum + Number(line.lineTotalCents || line.line_total_cents || 0), 0)
    : null;
  const totalCents = lineTotalCents ?? (quote.unit_price_cents && totalQty
    ? quote.unit_price_cents * totalQty
    : null);
  return (
    <SectionCard className="border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/20">
      <div className="flex items-center gap-2 mb-3">
        <Send className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
        <h3 className="text-sm font-semibold text-emerald-800 dark:text-emerald-200">Your Submitted Quote</h3>
        {quote.quote_number && <span className="font-mono text-xs text-emerald-500">{quote.quote_number}</span>}
        <StatusBadge status={quote.status || "SUBMITTED"} />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
        <div>
          <p className="text-emerald-500 mb-0.5">Unit Price</p>
          <p className="font-bold text-emerald-800 dark:text-emerald-200">{fmtPeso(quote.unit_price_cents)}</p>
        </div>
        <div>
          <p className="text-emerald-500 mb-0.5">Quantity</p>
          <p className="font-medium text-emerald-700 dark:text-emerald-300">{quote.quantity ?? "As requested"}</p>
        </div>
        <div>
          <p className="text-emerald-500 mb-0.5">Delivery</p>
          <p className="font-medium text-emerald-700 dark:text-emerald-300">
            {quote.delivery_days ? `${quote.delivery_days} days` : "—"}
          </p>
        </div>
        <div>
          <p className="text-emerald-500 mb-0.5">Estimated Total</p>
          <p className="font-bold text-emerald-800 dark:text-emerald-200">
            {totalCents ? fmtPeso(totalCents) : "—"}
          </p>
        </div>
      </div>
      {Array.isArray(quote.lines) && quote.lines.length > 0 && (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-emerald-200/70 text-left text-emerald-600 dark:border-emerald-800/60 dark:text-emerald-400">
                <th className="pb-2 pr-3 font-medium">Variant</th>
                <th className="pb-2 pr-3 text-right font-medium">Qty</th>
                <th className="pb-2 pr-3 text-right font-medium">Unit Price</th>
                <th className="pb-2 text-right font-medium">Line Total</th>
              </tr>
            </thead>
            <tbody>
              {quote.lines.map((line) => (
                <tr key={line.id || line.rfqLineId} className="border-b border-emerald-100/70 last:border-0 dark:border-emerald-900/40">
                  <td className="py-2 pr-3 text-emerald-800 dark:text-emerald-200">{line.variantTitle || line.variant_title || line.variantSku || "Requested item"}</td>
                  <td className="py-2 pr-3 text-right text-emerald-700 dark:text-emerald-300">{line.quotedQuantity || line.quoted_quantity}</td>
                  <td className="py-2 pr-3 text-right text-emerald-700 dark:text-emerald-300">{fmtPeso(line.quotedUnitPriceCents || line.quoted_unit_price_cents)}</td>
                  <td className="py-2 text-right font-semibold text-emerald-900 dark:text-emerald-100">{fmtPeso(line.lineTotalCents || line.line_total_cents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {quote.notes && (
        <div className="mt-3 rounded-lg bg-emerald-100/50 dark:bg-emerald-900/30 p-2.5 text-xs text-emerald-700 dark:text-emerald-300">
          <p className="text-[10px] text-emerald-500 mb-0.5 uppercase tracking-wide font-medium">Your notes</p>
          {quote.notes}
        </div>
      )}
    </SectionCard>
  );
}

/* ─── Quote Form Validation ───────────────────────────────────────── */

function validateQuoteFields(form, { variantQuote = false } = {}) {
  const errors = {};
  const price = parseFloat(form.unitPrice);

  if (!variantQuote && (!form.unitPrice || !String(form.unitPrice).trim())) {
    errors.unitPrice = 'Unit price is required.';
  } else if (!variantQuote && (isNaN(price) || price <= 0)) {
    errors.unitPrice = 'Enter a positive unit price (e.g. 125.00).';
  } else if (!variantQuote && price > 10_000_000) {
    errors.unitPrice = 'Unit price cannot exceed ₱10,000,000.';
  }

  if (!variantQuote && String(form.quantity || '').trim()) {
    const qty = Number(form.quantity);
    if (!Number.isInteger(qty) || qty <= 0) {
      errors.quantity = 'Quantity must be a positive whole number.';
    } else if (qty > 1_000_000) {
      errors.quantity = 'Quantity cannot exceed 1,000,000.';
    }
  }

  if (String(form.deliveryDays || '').trim()) {
    const days = Number(form.deliveryDays);
    if (!Number.isInteger(days) || days < 0) {
      errors.deliveryDays = 'Delivery days must be a whole number from 0 to 3,650.';
    } else if (days > 3_650) {
      errors.deliveryDays = 'Cannot exceed 3,650 days (10 years).';
    }
  }

  if (form.notes && form.notes.length > 2000) {
    errors.notes = `Too long — ${form.notes.length}/2,000 characters.`;
  }

  if (!form.termsAcknowledged) {
    errors.termsAcknowledged = "You must confirm the quotation terms before submitting.";
  }

  return errors;
}

function buildLineQuoteForm(lines) {
  return (Array.isArray(lines) ? lines : []).map((line, index) => ({
    rfqLineId: line.rfqLineId || line.id,
    label: line.variantTitle || line.variant_title || line.product_name || `Line ${index + 1}`,
    sku: line.variantSku || line.variant_sku || "",
    requestedQuantity: Number(line.requestedQuantity || line.requested_quantity || 0),
    quotedQuantity: String(line.requestedQuantity || line.requested_quantity || ""),
    quotedUnitPrice: "",
    supplierNote: "",
  }));
}

function validateLineQuoteForm(lines) {
  const errors = {};
  lines.forEach((line) => {
    const lineErrors = {};
    const qty = Number(line.quotedQuantity);
    const price = Number(line.quotedUnitPrice);

    if (!Number.isInteger(qty) || qty <= 0) {
      lineErrors.quotedQuantity = "Enter a positive whole number.";
    }
    if (!Number.isFinite(price) || price <= 0) {
      lineErrors.quotedUnitPrice = "Enter a positive price.";
    } else if (price > 10_000_000) {
      lineErrors.quotedUnitPrice = "Cannot exceed ₱10,000,000.";
    }

    if (Object.keys(lineErrors).length) {
      errors[line.rfqLineId] = lineErrors;
    }
  });
  return errors;
}

function summarizeLineQuoteForm(lines) {
  return lines.reduce((acc, line) => {
    const qty = Number(line.quotedQuantity || 0);
    const priceCents = Math.round(Number(line.quotedUnitPrice || 0) * 100);
    acc.totalQty += qty;
    acc.totalCents += qty * priceCents;
    return acc;
  }, { totalQty: 0, totalCents: 0 });
}

/* ═══════════════════════════════════════════════════════════
   Main Component
   ═══════════════════════════════════════════════════════════ */

export default function SupplierRFQs() {
  const [rfqs, setRFQs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [quoteForm, setQuoteForm] = useState({ unitPrice: "", quantity: "", deliveryDays: "", notes: "", termsAcknowledged: false });
  const [lineQuoteForm, setLineQuoteForm] = useState([]);
  const [bulkLineNote, setBulkLineNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await supplierRFQAPI.list({ limit: 200 });
      setRFQs(res?.data || []);
    } catch { setRFQs([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  const openDetail = async (rfq) => {
    setSelected(rfq);
    setDetail(null);
    setDetailLoading(true);
    setQuoteForm({ unitPrice: "", quantity: "", deliveryDays: "", notes: "", termsAcknowledged: false });
    setLineQuoteForm([]);
    setBulkLineNote("");
    setSubmitAttempted(false);
    try {
      const res = await supplierRFQAPI.getById(rfq.id);
      setDetail(res?.data || null);
    } catch { setDetail(null); }
    finally { setDetailLoading(false); }
  };

  const requestedLines = useMemo(() => {
    if (!detail) return [];
    if (Array.isArray(detail.lines) && detail.lines.length) return detail.lines;
    return Array.isArray(detail.prItems) ? detail.prItems : [];
  }, [detail]);

  const isVariantQuote = requestedLines.length > 1;

  // Total requested quantity from RFQ lines (or fallback to header)
  const totalQty = useMemo(() => {
    if (!detail) return 0;
    if (requestedLines.length) {
      return requestedLines.reduce((sum, line) => sum + Number(line.requestedQuantity || line.requested_quantity || 0), 0);
    }
    return detail.requested_quantity || 0;
  }, [detail, requestedLines]);

  useEffect(() => {
    if (!detail || detail.myQuote) return;
    setQuoteForm((current) => {
      const nextQuantity = current.quantity || (totalQty ? String(totalQty) : "");
      const nextDeliveryDays = current.deliveryDays || deriveDeliveryDays(detail.expected_delivery_at);

      if (nextQuantity === current.quantity && nextDeliveryDays === current.deliveryDays) {
        return current;
      }

      return {
        ...current,
        quantity: nextQuantity,
        deliveryDays: nextDeliveryDays,
      };
    });
  }, [detail, totalQty]);

  useEffect(() => {
    if (!detail || detail.myQuote || !isVariantQuote) return;
    setLineQuoteForm((current) => (current.length ? current : buildLineQuoteForm(requestedLines)));
  }, [detail, isVariantQuote, requestedLines]);

  // Live-computed validation errors (only shown after first submit attempt)
  const liveErrors = useMemo(
    () => (submitAttempted ? validateQuoteFields(quoteForm, { variantQuote: isVariantQuote }) : {}),
    [isVariantQuote, quoteForm, submitAttempted],
  );
  const hasFormErrors = Object.keys(liveErrors).length > 0;
  const lineFormErrors = useMemo(
    () => (submitAttempted && isVariantQuote ? validateLineQuoteForm(lineQuoteForm) : {}),
    [isVariantQuote, lineQuoteForm, submitAttempted],
  );
  const hasLineFormErrors = Object.keys(lineFormErrors).length > 0;

  const applyRequestedQuantities = () => {
    setLineQuoteForm((current) => current.map((line) => ({
      ...line,
      quotedQuantity: String(line.requestedQuantity || ""),
    })));
  };

  const applyBulkLineNote = () => {
    setLineQuoteForm((current) => current.map((line) => ({
      ...line,
      supplierNote: bulkLineNote,
    })));
  };

  const handleSubmitQuote = async (e) => {
    e.preventDefault();
    setSubmitAttempted(true);

    // Client-side validation gate — must pass before Swal confirmation
    const quoteErrors = validateQuoteFields(quoteForm, { variantQuote: isVariantQuote });
    const variantLineErrors = isVariantQuote ? validateLineQuoteForm(lineQuoteForm) : {};
    if (Object.keys(quoteErrors).length > 0 || Object.keys(variantLineErrors).length > 0) return;

    if (isVariantQuote) {
      const summary = summarizeLineQuoteForm(lineQuoteForm);
      const confirm = await Swal.fire({
        title: "Submit Variant Quote?",
        html: `
          <div class="text-left text-sm space-y-1">
            <p><strong>Quoted lines:</strong> ${lineQuoteForm.length}</p>
            <p><strong>Total quantity:</strong> ${summary.totalQty}</p>
            <p><strong>Delivery:</strong> ${quoteForm.deliveryDays ? quoteForm.deliveryDays + " days" : "Not specified"}</p>
            <p><strong>Estimated Total:</strong> ${fmtPeso(summary.totalCents)}</p>
          </div>
          <p class="text-xs text-gray-500 mt-3">This action cannot be undone.</p>
        `,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Submit Quote",
        confirmButtonColor: "#2563eb",
      });
      if (!confirm.isConfirmed) return;

      setSubmitting(true);
      try {
        await supplierRFQAPI.submitQuote(selected.id, {
          deliveryDays: quoteForm.deliveryDays ? parseInt(quoteForm.deliveryDays, 10) : undefined,
          notes: quoteForm.notes || undefined,
          termsAcknowledged: true,
          lines: lineQuoteForm.map((line) => ({
            rfqLineId: line.rfqLineId,
            quotedQuantity: parseInt(line.quotedQuantity, 10),
            quotedUnitPriceCents: Math.round(parseFloat(line.quotedUnitPrice) * 100),
            supplierNote: line.supplierNote || undefined,
          })),
        });
        Swal.fire({ icon: "success", title: "Quote Submitted", text: "The business will review your quote.", timer: 2000, showConfirmButton: false });
        setSelected(null);
        fetchList();
      } catch (err) {
        const msg = err?.response?.data?.message || err?.message || "Could not submit quote";
        Swal.fire({ icon: "error", title: "Submission Failed", text: msg });
      } finally { setSubmitting(false); }
      return;
    }

    const unitPriceCents = Math.round(parseFloat(quoteForm.unitPrice) * 100);
    const quotedQuantity = Number(quoteForm.quantity || totalQty || 0);
    const totalCents = unitPriceCents * quotedQuantity;
    const confirm = await Swal.fire({
      title: "Submit Quote?",
      html: `
        <div class="text-left text-sm space-y-1">
          <p><strong>Unit Price:</strong> ${fmtPeso(unitPriceCents)}</p>
          <p><strong>Quantity:</strong> ${quoteForm.quantity || totalQty + " (as requested)"}</p>
          <p><strong>Delivery:</strong> ${quoteForm.deliveryDays ? quoteForm.deliveryDays + " days" : "Not specified"}</p>
          ${totalCents ? `<p><strong>Estimated Total:</strong> ${fmtPeso(totalCents)}</p>` : ""}
        </div>
        <p class="text-xs text-gray-500 mt-3">This action cannot be undone.</p>
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Submit Quote",
      confirmButtonColor: "#2563eb",
    });
    if (!confirm.isConfirmed) return;

    setSubmitting(true);
    try {
      await supplierRFQAPI.submitQuote(selected.id, {
        unitPriceCents,
        quantity: quoteForm.quantity ? parseInt(quoteForm.quantity, 10) : undefined,
        deliveryDays: quoteForm.deliveryDays ? parseInt(quoteForm.deliveryDays, 10) : undefined,
        notes: quoteForm.notes || undefined,
        termsAcknowledged: true,
      });
      Swal.fire({ icon: "success", title: "Quote Submitted", text: "The business will review your quote.", timer: 2000, showConfirmButton: false });
      setSelected(null);
      fetchList();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Could not submit quote";
      Swal.fire({ icon: "error", title: "Submission Failed", text: msg });
    } finally { setSubmitting(false); }
  };

  const handleDeclineRFQ = async () => {
    if (!selected?.id) return;

    const { value, isConfirmed } = await Swal.fire({
      title: "Decline RFQ?",
      input: "textarea",
      inputLabel: "Reason for decline",
      inputPlaceholder: "Optional reason to share with the business",
      inputAttributes: { maxlength: 1000 },
      showCancelButton: true,
      confirmButtonText: "Decline RFQ",
      confirmButtonColor: "#dc2626",
    });
    if (!isConfirmed) return;

    setSubmitting(true);
    try {
      await supplierRFQAPI.decline(selected.id, { reason: value || undefined });
      Swal.fire({
        icon: "success",
        title: "RFQ declined",
        text: "The business has been updated with your decline status.",
        timer: 1800,
        showConfirmButton: false,
      });
      setSelected(null);
      fetchList();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Could not decline this RFQ";
      Swal.fire({ icon: "error", title: "Decline Failed", text: msg });
    } finally {
      setSubmitting(false);
    }
  };

  /* ─── Detail View ────────────────────────────────────── */

  if (selected) {
    const dl = detail ? deadlineInfo(detail.deadline_at) : null;
    const isDeadlinePassed = detail?.deadline_at ? new Date() > endOfDeadlineDay(detail.deadline_at) : false;
    const invitationOpen = detail ? OPEN_INVITATION_STATUSES.includes(detail.invitation_status) : false;
    const quotingStatusButDeadlinePassed = detail && !detail.myQuote && invitationOpen && QUOTEABLE_STATUSES.includes(detail.status) && isDeadlinePassed;
    const canQuote = detail && !detail.myQuote && invitationOpen && QUOTEABLE_STATUSES.includes(detail.status) && !isDeadlinePassed;

    return (
      <div className="operations-page max-w-4xl">
        <button onClick={() => setSelected(null)} className="flex items-center gap-1 text-xs text-blue-600 hover:underline dark:text-blue-400">
          <ChevronLeft className="h-3 w-3" /> Back to RFQs
        </button>

        {detailLoading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-blue-500" /></div>
        ) : !detail ? (
          <div className="rounded-xl bg-red-50 p-4 text-xs text-red-700 dark:bg-red-950/30 dark:text-red-300 flex items-center gap-2">
            <AlertCircle className="h-4 w-4" /> Could not load RFQ details.
          </div>
        ) : (
          <>
            {/* ── Section 1: RFQ Summary ─────────────────── */}
            <SectionCard>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-50">
                    {detail.title || getRFQLabel(detail)}
                  </h2>
                  <StatusBadge status={detail.status} context="supplier" />
                </div>
                <span className="text-[10px] text-neutral-400 font-mono">{getRFQLabel(detail)}</span>
              </div>

              {/* Key metadata grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                <InfoRow icon={Building2} label="Business" value={detail.business_name} />
                <InfoRow icon={Package} label="Product" value={detail.product_name} />
                <InfoRow icon={Hash} label="Total Quantity" value={`${totalQty} units`} />
                <InfoRow icon={Calendar} label="Deadline" value={
                  detail.deadline_at
                    ? <span className="flex items-center gap-1.5">
                        {fmtDate(detail.deadline_at)}
                        {dl && <span className={`text-[10px] font-semibold ${dl.className}`}>({dl.label})</span>}
                      </span>
                    : "—"
                } />
                {detail.expected_delivery_at && (
                  <InfoRow icon={Clock} label="Expected Delivery" value={fmtDate(detail.expected_delivery_at)} />
                )}
                {detail.rfq_type && (
                  <InfoRow icon={Tag} label="Type" value={detail.rfq_type} />
                )}
                {detail.currency && (
                  <InfoRow icon={DollarSign} label="Currency" value={detail.currency} />
                )}
                {detail.product_sku && (
                  <InfoRow icon={Tag} label="Product SKU" value={detail.product_sku} muted />
                )}
              </div>

              {/* Procurement reason */}
              {detail.pr_reason && (
                <div className="mt-4 rounded-lg bg-amber-50 dark:bg-amber-950/20 p-3 text-xs text-amber-800 dark:text-amber-200">
                  <p className="text-[10px] text-amber-500 dark:text-amber-400 mb-0.5 uppercase tracking-wide font-medium">Procurement Reason</p>
                  {detail.pr_reason}
                </div>
              )}

              {/* Product description */}
              {detail.product_description && (
                <div className="mt-3 rounded-lg bg-neutral-50 dark:bg-neutral-800/50 p-3 text-xs text-neutral-600 dark:text-neutral-300">
                  <p className="text-[10px] text-neutral-400 dark:text-neutral-500 mb-0.5 uppercase tracking-wide font-medium">Product Description</p>
                  {detail.product_description}
                </div>
              )}

              {/* Notes from business */}
              {detail.notes && (
                <div className="mt-3 rounded-lg bg-neutral-50 dark:bg-neutral-800/50 p-3 text-xs text-neutral-600 dark:text-neutral-300">
                  <p className="text-[10px] text-neutral-400 dark:text-neutral-500 mb-0.5 uppercase tracking-wide font-medium">Notes from Business</p>
                  {detail.notes}
                </div>
              )}
            </SectionCard>

            {/* ── Section 2: Variant Breakdown ───────────── */}
            <VariantBreakdown items={requestedLines} productName={detail.product_name} />

            {/* ── Section 3: Quote (submitted or form) ───── */}
            {detail.myQuote ? (
              <SubmittedQuote quote={detail.myQuote} totalQty={totalQty} />
            ) : quotingStatusButDeadlinePassed ? (
              <div className="rounded-xl border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/30 p-4 text-xs text-red-700 dark:text-red-300 flex items-center gap-2">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>The deadline for this RFQ has passed. Quotes are no longer accepted.</span>
              </div>
            ) : canQuote ? (
              <SectionCard>
                <div className="flex items-center gap-2 mb-1">
                  <DollarSign className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-50">Submit Your Quote</h3>
                </div>

                <div className="flex items-start gap-2 rounded-lg bg-blue-50 dark:bg-blue-950/30 p-3 text-xs text-blue-700 dark:text-blue-300 mt-3 mb-4">
                  <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                  <span>
                    {isVariantQuote
                      ? <>Quote each requested variant below. The business will compare your line pricing and total quoted amount across suppliers.</>
                      : <>Enter your best unit price. The business will compare all submitted quotes. Total requested: <strong>{totalQty} units</strong>.</>}
                  </span>
                </div>

                <form onSubmit={handleSubmitQuote} className="space-y-4">
                  {isVariantQuote ? (
                    <div className="space-y-4">
                      <div className="grid gap-3 rounded-xl border border-neutral-200 bg-neutral-50 p-3 text-xs dark:border-neutral-700 dark:bg-neutral-800/50 lg:grid-cols-[auto,1fr,1fr]">
                        <button
                          type="button"
                          onClick={applyRequestedQuantities}
                          className="rounded-lg border border-neutral-200 bg-white px-3 py-2 font-medium text-neutral-700 transition hover:bg-neutral-50 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-200 dark:hover:bg-neutral-800"
                        >
                          Match requested qty
                        </button>
                        <div className="rounded-lg border border-neutral-200 bg-white px-3 py-2 dark:border-neutral-700 dark:bg-neutral-900">
                          <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                            Delivery (days)
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="1"
                            value={quoteForm.deliveryDays}
                            onChange={(e) => setQuoteForm((current) => ({ ...current, deliveryDays: sanitizeIntegerInput(e.target.value) }))}
                            className={`w-full rounded-lg border px-2 py-1.5 text-right text-xs outline-none focus:ring-2 focus:ring-blue-400 dark:bg-neutral-800 dark:text-neutral-100 ${liveErrors.deliveryDays ? "border-red-400 dark:border-red-500" : "border-neutral-200 dark:border-neutral-700"}`}
                            placeholder="Promised lead time"
                          />
                          {liveErrors.deliveryDays && (
                            <p className="mt-1 flex items-center gap-1 text-xs text-red-500 dark:text-red-400">
                              <AlertCircle className="h-3 w-3 flex-shrink-0" />{liveErrors.deliveryDays}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={bulkLineNote}
                            onChange={(e) => setBulkLineNote(e.target.value)}
                            className="w-full rounded-lg border border-neutral-200 px-2 py-1.5 text-xs outline-none focus:ring-2 focus:ring-blue-400 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100"
                            placeholder="Bulk note for all variant lines"
                          />
                          <button
                            type="button"
                            onClick={applyBulkLineNote}
                            className="rounded-lg border border-neutral-200 bg-white px-3 py-2 font-medium text-neutral-700 transition hover:bg-neutral-50 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-200 dark:hover:bg-neutral-800"
                          >
                            Apply note
                          </button>
                        </div>
                      </div>
                      <div className="overflow-x-auto rounded-xl border border-neutral-200 dark:border-neutral-700">
                        <table className="w-full text-xs">
                          <thead className="bg-neutral-50 text-neutral-500 dark:bg-neutral-900/60 dark:text-neutral-400">
                            <tr>
                              <th className="px-3 py-2 text-left font-medium">Variant</th>
                              <th className="px-3 py-2 text-right font-medium">Requested</th>
                              <th className="px-3 py-2 text-right font-medium">Quoted Qty</th>
                              <th className="px-3 py-2 text-right font-medium">Unit Price</th>
                              <th className="px-3 py-2 text-right font-medium">Line Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {lineQuoteForm.map((line) => {
                              const lineErrors = lineFormErrors[line.rfqLineId] || {};
                              const quotedQty = Number(line.quotedQuantity || 0);
                              const lineTotal = Math.round(Number(line.quotedUnitPrice || 0) * 100) * quotedQty;
                              return (
                                <Fragment key={line.rfqLineId}>
                                  <tr className="border-t border-neutral-100 align-top dark:border-neutral-800">
                                    <td className="px-3 py-3">
                                      <div className="font-medium text-neutral-800 dark:text-neutral-100">{line.label}</div>
                                      <div className="text-[10px] text-neutral-400 dark:text-neutral-500">{line.sku || "No SKU"}</div>
                                    </td>
                                    <td className="px-3 py-3 text-right font-medium text-neutral-700 dark:text-neutral-200">{line.requestedQuantity}</td>
                                    <td className="px-3 py-3">
                                      <input
                                        type="number"
                                        min="1"
                                        step="1"
                                        value={line.quotedQuantity}
                                        onChange={(e) => setLineQuoteForm((current) => current.map((item) => item.rfqLineId === line.rfqLineId ? { ...item, quotedQuantity: sanitizeIntegerInput(e.target.value) } : item))}
                                        className={`w-24 rounded-lg border px-2 py-1.5 text-right text-xs outline-none focus:ring-2 focus:ring-blue-400 dark:bg-neutral-800 dark:text-neutral-100 ${lineErrors.quotedQuantity ? "border-red-400 dark:border-red-500" : "border-neutral-200 dark:border-neutral-700"}`}
                                      />
                                      {lineErrors.quotedQuantity && (
                                        <p className="mt-1 text-right text-[11px] text-red-500 dark:text-red-400">{lineErrors.quotedQuantity}</p>
                                      )}
                                    </td>
                                    <td className="px-3 py-3">
                                      <input
                                        type="number"
                                        min="0.01"
                                        step="0.01"
                                        value={line.quotedUnitPrice}
                                        onChange={(e) => setLineQuoteForm((current) => current.map((item) => item.rfqLineId === line.rfqLineId ? { ...item, quotedUnitPrice: sanitizePriceInput(e.target.value) } : item))}
                                        className={`w-28 rounded-lg border px-2 py-1.5 text-right text-xs outline-none focus:ring-2 focus:ring-blue-400 dark:bg-neutral-800 dark:text-neutral-100 ${lineErrors.quotedUnitPrice ? "border-red-400 dark:border-red-500" : "border-neutral-200 dark:border-neutral-700"}`}
                                      />
                                      {lineErrors.quotedUnitPrice && (
                                        <p className="mt-1 text-right text-[11px] text-red-500 dark:text-red-400">{lineErrors.quotedUnitPrice}</p>
                                      )}
                                    </td>
                                    <td className="px-3 py-3 text-right font-semibold text-neutral-800 dark:text-neutral-100">{lineTotal ? fmtPeso(lineTotal) : "—"}</td>
                                  </tr>
                                  <tr className="border-b border-neutral-100 dark:border-neutral-800">
                                    <td colSpan={5} className="px-3 pb-3">
                                      <textarea
                                        rows={2}
                                        value={line.supplierNote}
                                        onChange={(e) => setLineQuoteForm((current) => current.map((item) => item.rfqLineId === line.rfqLineId ? { ...item, supplierNote: e.target.value } : item))}
                                        className="w-full rounded-lg border border-neutral-200 px-2 py-1.5 text-xs outline-none focus:ring-2 focus:ring-blue-400 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100"
                                        placeholder="Optional note for this variant"
                                      />
                                    </td>
                                  </tr>
                                </Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {/* Unit Price */}
                    <div>
                      <label className="mb-1 block text-xs font-medium text-neutral-700 dark:text-neutral-300">
                        Unit Price (₱) <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        max="10000000"
                        value={quoteForm.unitPrice}
                        onChange={(e) => setQuoteForm((f) => ({ ...f, unitPrice: sanitizePriceInput(e.target.value) }))}
                        onKeyDown={(e) => {
                          if (["-", "+", "e", "E"].includes(e.key)) e.preventDefault();
                        }}
                        className={`w-full rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-400 bg-white dark:bg-neutral-800 dark:text-neutral-100 ${
                          liveErrors.unitPrice
                            ? "border-red-400 dark:border-red-500"
                            : "border-neutral-200 dark:border-neutral-700"
                        }`}
                        placeholder="0.00"
                      />
                      {liveErrors.unitPrice && (
                        <p className="mt-1 flex items-center gap-1 text-xs text-red-500 dark:text-red-400">
                          <AlertCircle className="h-3 w-3 flex-shrink-0" />{liveErrors.unitPrice}
                        </p>
                      )}
                    </div>

                    {/* Quantity */}
                    <div>
                      <label className="mb-1 block text-xs font-medium text-neutral-700 dark:text-neutral-300">
                        Quantity Available
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="1000000"
                        step="1"
                        value={quoteForm.quantity}
                        onChange={(e) => setQuoteForm((f) => ({ ...f, quantity: sanitizeIntegerInput(e.target.value) }))}
                        onKeyDown={(e) => {
                          if (["-", "+", ".", "e", "E"].includes(e.key)) e.preventDefault();
                        }}
                        className={`w-full rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-400 bg-white dark:bg-neutral-800 dark:text-neutral-100 ${
                          liveErrors.quantity
                            ? "border-red-400 dark:border-red-500"
                            : "border-neutral-200 dark:border-neutral-700"
                        }`}
                        placeholder={`${totalQty} (requested)`}
                      />
                      {liveErrors.quantity && (
                        <p className="mt-1 flex items-center gap-1 text-xs text-red-500 dark:text-red-400">
                          <AlertCircle className="h-3 w-3 flex-shrink-0" />{liveErrors.quantity}
                        </p>
                      )}
                    </div>

                    {/* Delivery Days */}
                    <div>
                      <label className="mb-1 block text-xs font-medium text-neutral-700 dark:text-neutral-300">
                        Delivery (days)
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="3650"
                        step="1"
                        value={quoteForm.deliveryDays}
                        onChange={(e) => setQuoteForm((f) => ({ ...f, deliveryDays: sanitizeIntegerInput(e.target.value) }))}
                        onKeyDown={(e) => {
                          if (["-", "+", ".", "e", "E"].includes(e.key)) e.preventDefault();
                        }}
                        className={`w-full rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-400 bg-white dark:bg-neutral-800 dark:text-neutral-100 ${
                          liveErrors.deliveryDays
                            ? "border-red-400 dark:border-red-500"
                            : "border-neutral-200 dark:border-neutral-700"
                        }`}
                        placeholder="e.g. 14"
                      />
                      {liveErrors.deliveryDays && (
                        <p className="mt-1 flex items-center gap-1 text-xs text-red-500 dark:text-red-400">
                          <AlertCircle className="h-3 w-3 flex-shrink-0" />{liveErrors.deliveryDays}
                        </p>
                      )}
                    </div>
                  </div>
                  )}

                  {/* Notes with character counter */}
                  <div>
                    <div className="mb-1 flex items-center justify-between">
                      <label className="text-xs font-medium text-neutral-700 dark:text-neutral-300">Notes</label>
                      <span className={`text-[10px] tabular-nums ${
                        quoteForm.notes.length > 2000
                          ? "text-red-500 dark:text-red-400"
                          : "text-neutral-400 dark:text-neutral-500"
                      }`}>
                        {quoteForm.notes.length}/2,000
                      </span>
                    </div>
                    <textarea
                      rows={2}
                      maxLength={2000}
                      value={quoteForm.notes}
                      onChange={(e) => setQuoteForm((f) => ({ ...f, notes: e.target.value }))}
                      className={`w-full rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-400 dark:bg-neutral-800 dark:text-neutral-100 resize-none ${
                        liveErrors.notes
                          ? "border-red-400 dark:border-red-500"
                          : "border-neutral-200 dark:border-neutral-700"
                      }`}
                      placeholder="Additional terms, conditions, or notes…"
                    />
                    {liveErrors.notes && (
                      <p className="mt-1 flex items-center gap-1 text-xs text-red-500 dark:text-red-400">
                        <AlertCircle className="h-3 w-3 flex-shrink-0" />{liveErrors.notes}
                      </p>
                    )}
                  </div>

                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950/20 dark:text-amber-200">
                    <h4 className="text-sm font-semibold text-amber-900 dark:text-amber-100">Quotation Terms & Acknowledgement</h4>
                    <p className="mt-2 leading-5">
                      By submitting this quotation, you confirm that the quoted price, quantity, delivery timing, and notes are accurate and currently supportable by your business. You understand that the requesting business may rely on this quotation for comparison and procurement decisions.
                    </p>
                    <label className="mt-3 flex items-start gap-2 text-neutral-800 dark:text-neutral-100">
                      <input
                        type="checkbox"
                        checked={quoteForm.termsAcknowledged}
                        onChange={(e) => setQuoteForm((current) => ({ ...current, termsAcknowledged: e.target.checked }))}
                        className="mt-0.5 h-4 w-4 rounded border-neutral-300 text-neutral-900 focus:ring-blue-400 dark:border-neutral-600 dark:bg-neutral-900"
                      />
                      <span>I confirm that this quotation is accurate and I agree to the quotation submission terms.</span>
                    </label>
                    {liveErrors.termsAcknowledged && (
                      <p className="mt-2 flex items-center gap-1 text-xs text-red-500 dark:text-red-400">
                        <AlertCircle className="h-3 w-3 flex-shrink-0" />{liveErrors.termsAcknowledged}
                      </p>
                    )}
                  </div>

                  {/* Live total estimate */}
                  {isVariantQuote ? (
                    <div className="rounded-lg bg-blue-50 dark:bg-blue-950/30 p-3 text-xs space-y-1">
                      <div className="flex justify-between">
                        <span className="text-blue-600 dark:text-blue-400">Quoted lines</span>
                        <span className="font-medium text-blue-800 dark:text-blue-200">{lineQuoteForm.length}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-blue-600 dark:text-blue-400">Total quantity</span>
                        <span className="font-medium text-blue-800 dark:text-blue-200">{summarizeLineQuoteForm(lineQuoteForm).totalQty}</span>
                      </div>
                      <div className="flex justify-between border-t border-blue-200 pt-1 dark:border-blue-800">
                        <span className="font-semibold text-blue-700 dark:text-blue-300">Estimated Total</span>
                        <span className="font-bold text-blue-900 dark:text-blue-100">{fmtPeso(summarizeLineQuoteForm(lineQuoteForm).totalCents)}</span>
                      </div>
                    </div>
                  ) : (
                    <QuoteSummary unitPrice={quoteForm.unitPrice} totalQty={Number(quoteForm.quantity || totalQty || 0)} />
                  )}

                  <div className="flex flex-wrap gap-3">
                    <button
                      type="submit"
                      disabled={submitting || !quoteForm.termsAcknowledged || (submitAttempted && (hasFormErrors || hasLineFormErrors))}
                      className="flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-700 transition disabled:opacity-50"
                    >
                      {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                      {submitting ? "Submitting…" : "Submit Quote"}
                    </button>
                    <button
                      type="button"
                      onClick={handleDeclineRFQ}
                      disabled={submitting}
                      className="rounded-xl border border-red-200 px-4 py-2.5 text-sm font-medium text-red-700 transition hover:bg-red-50 disabled:opacity-50 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-950/30"
                    >
                      Decline RFQ
                    </button>
                  </div>
                </form>
              </SectionCard>
            ) : (
              <div className="rounded-xl bg-neutral-50 dark:bg-neutral-800/50 p-4 text-xs text-neutral-500 dark:text-neutral-400 flex items-center gap-2">
                <Info className="h-4 w-4" />
                This RFQ is no longer accepting quotes (status: {detail.status}).
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  /* ─── List View ──────────────────────────────────────── */

  return (
    <div className="operations-page">
      <div className="operations-page-header">
        <h1 className="text-base font-semibold text-neutral-900 dark:text-neutral-50">Requests for Quotation</h1>
        <span className="text-xs text-neutral-400">{rfqs.length} total</span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-blue-500" /></div>
      ) : rfqs.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-neutral-200 dark:border-neutral-700 p-8 text-center">
          <FileText className="mx-auto h-8 w-8 text-neutral-300 dark:text-neutral-600 mb-2" />
          <p className="text-sm font-medium text-neutral-500 dark:text-neutral-400">No RFQs yet</p>
          <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-1">When businesses send you requests, they'll appear here.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {rfqs.map((rfq) => {
            const dl = deadlineInfo(rfq.deadline_at);
            return (
              <button
                key={rfq.id}
                onClick={() => openDetail(rfq)}
                className="operations-surface w-full p-4 text-left transition hover:border-neutral-300 dark:hover:border-neutral-600"
              >
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-neutral-900 dark:text-neutral-50">
                      {rfq.title || getRFQLabel(rfq)}
                    </span>
                    <span className="text-[10px] text-neutral-400 font-mono">{getRFQLabel(rfq)}</span>
                  </div>
                  <StatusBadge status={rfq.status} context="supplier" />
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-neutral-500 dark:text-neutral-400">
                  {rfq.product_name && <span className="flex items-center gap-1"><Package className="h-3 w-3" />{rfq.product_name}</span>}
                  {rfq.requested_quantity && <span>{rfq.requested_quantity} units</span>}
                  {rfq.deadline_at && (
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {fmtDate(rfq.deadline_at)}
                      {dl && <span className={`text-[10px] font-semibold ${dl.className}`}>({dl.label})</span>}
                    </span>
                  )}
                  {rfq.business_name && <span className="flex items-center gap-1"><Building2 className="h-3 w-3" />{rfq.business_name}</span>}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
